import { Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * One field in a card's form (brief §3: "label above field, 40px fields"):
 * the label as a 12px muted line, the control, and an optional line under it
 * — help, or a warning. Above, never inside: a `TextField` takes no `label`
 * in this app (CLAUDE.md).
 */
export type FormFieldProps = {
  label: string;
  /** Ties the label to its control, so clicking it focuses the field. */
  htmlFor?: string;
  /** Under the control: help, or a warning the caller has styled. */
  below?: ReactNode;
  children: ReactNode;
  automationId?: string;
};

export const FormField = ({ label, htmlFor, below, children, automationId }: FormFieldProps) => (
  <Stack sx={{ gap: 0.5, minWidth: 0 }} data-automation-id={automationId}>
    <Typography
      variant="label"
      component={htmlFor ? 'label' : 'span'}
      htmlFor={htmlFor}
      sx={{ color: 'var(--color-fog-80)' }}
    >
      {label}
    </Typography>
    {children}
    {below}
  </Stack>
);

/**
 * The brief's 40px field (§3), on a MUI `Select` or `TextField` — the theme's
 * `--input-height-medium`. MUI's own `small` size comes out at 37px with this
 * app's font, so the height is pinned rather than left to the padding.
 */
export const FIELD_HEIGHT_SX = {
  '&.MuiInputBase-root, & .MuiInputBase-root': {
    height: 'var(--input-height-medium)',
    boxSizing: 'border-box',
  },
} as const;

/** A line of help under a field. */
export const FieldHelp = ({ children }: { children: ReactNode }) => (
  <Typography variant="label" component="p" sx={{ color: 'var(--color-fog-80)' }}>
    {children}
  </Typography>
);
